import { useCallback, useEffect, useState } from "react"
import {
  clearSharedEvoFilters,
  defaultEvoFilters,
  getSharedFullNameFilter,
  getSharedMonthFilter,
  getSharedYearFilter,
  setSharedFullNameFilter,
  setSharedMonthFilter,
  setSharedYearFilter,
} from "utils/evoTripsUi"

export type EvoListFilters = {
  fullName?: string
  month?: string
  year?: string
  searchText?: string
}

const STORAGE_PREFIX = "evo-my-trips-filters:"

function mergeSharedFilters(parsed: EvoListFilters, defaults: EvoListFilters): EvoListFilters {
  const sharedName = getSharedFullNameFilter()
  const sharedMonth = getSharedMonthFilter()
  const sharedYear = getSharedYearFilter()
  const merged = { ...defaults, ...parsed }
  if (sharedName) merged.fullName = sharedName
  if (sharedMonth) merged.month = sharedMonth
  if (sharedYear) merged.year = sharedYear
  return merged
}

export function usePersistedFilters(
  storageKey: string,
  defaults: EvoListFilters = defaultEvoFilters()
) {
  const key = `${STORAGE_PREFIX}${storageKey}`

  const read = (): EvoListFilters => {
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) {
        return mergeSharedFilters({}, defaults)
      }
      const parsed = JSON.parse(raw) as EvoListFilters
      return mergeSharedFilters(parsed, defaults)
    } catch {
      return { ...defaults }
    }
  }

  const [filters, setFiltersState] = useState<EvoListFilters>(read)

  const persistShared = (next: EvoListFilters) => {
    if (next.fullName !== undefined) setSharedFullNameFilter(next.fullName)
    if (next.month !== undefined) setSharedMonthFilter(next.month)
    if (next.year !== undefined) setSharedYearFilter(next.year)
  }

  const persist = useCallback(
    (next: EvoListFilters) => {
      setFiltersState(next)
      try {
        sessionStorage.setItem(key, JSON.stringify(next))
        persistShared(next)
      } catch {
        /* ignore quota errors */
      }
    },
    [key]
  )

  const setFilters = useCallback(
    (patch: Partial<EvoListFilters> | ((prev: EvoListFilters) => EvoListFilters)) => {
      setFiltersState((prev) => {
        const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch }
        try {
          sessionStorage.setItem(key, JSON.stringify(next))
          persistShared(next)
        } catch {
          /* ignore */
        }
        return next
      })
    },
    [key]
  )

  const clearFilters = useCallback(() => {
    const cleared = { ...defaults, fullName: "", month: "", year: "" }
    clearSharedEvoFilters()
    persist(cleared)
  }, [defaults, persist])

  useEffect(() => {
    setFiltersState(read())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  return { filters, setFilters, clearFilters }
}
